/**
 * Configuration Service
 * Fetches configuration from backend and manages caching
 */

import { API_CONFIG } from '../config/constants';
import { BackendConfig, DEFAULT_BACKEND_CONFIG } from '../types/config';
import { normalizeCDDStatus } from '../utils/cddStatus';
import { logger } from '../utils/logger';
import { tryParseWireRiskLevelToUpper } from '../utils/riskRating';
import { ENTITY_TYPE_MAP } from './configServiceConfig';
import { eventCentral } from './eventCentral';
import socketService from './socketService';
import wsApi from './wsApi';

const CONFIG_CACHE_KEY = 'app_config_cache';
const CONFIG_CACHE_DURATION = 1000 * 60 * 60; // 1 hour

// Track WebSocket authentication state
let wsAuthenticatedFlag = false;

/**
 * Set WebSocket authentication state after successful Cognito login
 */
export function setWebSocketAuthenticated(authenticated: boolean): void {
  wsAuthenticatedFlag = authenticated;
}

/**
 * Check if WebSocket is authenticated
 */
export function isWebSocketAuthenticated(): boolean {
  return wsAuthenticatedFlag;
}

/**
 * Ensure WebSocket is connected and ready
 */
function ensureWebSocketConnected(): void {
  const socket = socketService.getSocket();

  if (!socket) {
    throw new Error('⚠️ WebSocket not initialized');
  }

  if (socket.readyState !== WebSocket.OPEN) {
    throw new Error('⚠️ WebSocket not connected - Reconnecting...');
  }
}

interface CachedConfig {
  version: number;
  data: BackendConfig;
  timestamp: number;
}

const CONFIG_CACHE_VERSION = 4;

let currentConfig: BackendConfig | null = null;
let configPromise: Promise<BackendConfig> | null = null;

function isCacheValid(cached: CachedConfig): boolean {
  if (cached.version !== CONFIG_CACHE_VERSION) {
    return false;
  }
  const age = Date.now() - cached.timestamp;
  return age < CONFIG_CACHE_DURATION;
}

function getCachedConfig(): BackendConfig | null {
  try {
    const cached = localStorage.getItem(CONFIG_CACHE_KEY);
    if (!cached) return null;
    const parsed: CachedConfig = JSON.parse(cached);
    if (isCacheValid(parsed)) return parsed.data;
    localStorage.removeItem(CONFIG_CACHE_KEY);
    return null;
  } catch (error) {
    return null;
  }
}

function setCachedConfig(config: BackendConfig): void {
  try {
    const cached: CachedConfig = {
      version: CONFIG_CACHE_VERSION,
      data: config,
      timestamp: Date.now(),
    };
    localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify(cached));
  } catch (error) {
  }
}

export async function loadConfig(): Promise<BackendConfig> {
  if (configPromise) return configPromise;
  if (currentConfig) return currentConfig;

  configPromise = (async () => {
    const cached = getCachedConfig();
    if (cached) {
      currentConfig = cached;
      return cached;
    }

    currentConfig = DEFAULT_BACKEND_CONFIG;
    configPromise = null;
    return DEFAULT_BACKEND_CONFIG;
  })();

  return configPromise;
}

export function getConfig(): BackendConfig | null {
  return currentConfig;
}

export async function reloadConfig(): Promise<BackendConfig> {
  currentConfig = null;
  configPromise = null;
  currentConfig = DEFAULT_BACKEND_CONFIG;
  return DEFAULT_BACKEND_CONFIG;
}

export function clearConfigCache(): void {
  currentConfig = null;
  configPromise = null;
  localStorage.removeItem(CONFIG_CACHE_KEY);
}

export function isFeatureEnabled(featureId: string): boolean {
  if (!currentConfig) return true;
  return currentConfig.features[featureId]?.enabled ?? true;
}

export function getEnabledTabs() {
  if (!currentConfig) {
    return [];
  }
  return currentConfig.navigationTabs
    .filter((tab) => tab.enabled)
    .sort((a, b) => a.order - b.order);
}

export function applyThemeColors(config: BackendConfig): void {
  const root = document.documentElement;
  const theme = config.theme;

  root.style.setProperty('--primary-color', theme.primaryColor);
  root.style.setProperty('--secondary-color', theme.secondaryColor);
  root.style.setProperty('--accent-color', theme.accentColor);
  root.style.setProperty('--bg-color', theme.backgroundColor);
  root.style.setProperty('--text-color', theme.textColor);
  root.style.setProperty('--border-color', theme.borderColor);
  root.style.setProperty('--success-color', theme.successColor);
  root.style.setProperty('--warning-color', theme.warningColor);
  root.style.setProperty('--error-color', theme.errorColor);

  const gradient = `linear-gradient(135deg, ${theme.primaryColor} 0%, ${theme.secondaryColor} 100%)`;
  root.style.setProperty('--gradient-primary', gradient);

  if (theme.fontFamily) {
    root.style.setProperty('--font-family', theme.fontFamily);
  }

  if (config.companyLogo) {
    const favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement;
    if (favicon) favicon.href = config.companyLogo;
  }

  document.title = config.appName;

  if (config.ui.enableDarkMode) {
    document.documentElement.classList.add('dark-mode');
  } else {
    document.documentElement.classList.remove('dark-mode');
  }
}

/**
 * Fetch dashboard data via WebSocket
 */
export async function fetchDashboardData(): Promise<{ data: any; source: 'real' }> {
  ensureWebSocketConnected();
  const response = await eventCentral.sendRequest(socketService, 'QueryDashboard', {}, API_CONFIG.REQUEST_TIMEOUT);
  return { data: response.QueryDashboardResponse ?? response.data, source: 'real' };
}

/**
 * Map a QueryCasesResponse case entry to the blotter CDDRecord shape.
 * The response is a full case object — fields come from entity/persons/riskAssessment.
 */
export function mapRawToBlotterRecord(raw: any): any {
  const isCompany = (raw.workflowType || '').startsWith('BUSINESSENTITY') ||
    (raw.entityType || '').toLowerCase() === 'company';

  // For companies prefer entityName if the backend sends it, otherwise fall back to c2Name
  const customer = isCompany
    ? (raw.entityName || raw.legalName || raw.c2Name || '')
    : (raw.c2Name || '');

  return {
    id: raw.id || raw.caseId || raw.referenceNumber || '',
    reference: raw.referenceNumber || `CDD-${raw.id || ''}`,
    // AL-417: how many people are outstanding, and how many of those the C1 can actually chase.
    // Passed through as-is, including undefined. The backend omits these rather than sending zero
    // when the rollup could not be computed, and coercing that to 0 here would turn "we do not know"
    // into "nothing outstanding" — a false all-clear.
    totalPeople: raw.totalPeople,
    outstandingPeople: raw.outstandingPeople,
    customer,
    abn: raw.entityCode || raw.abn || '',
    type: ENTITY_TYPE_MAP[raw.workflowType] || raw.entityType || 'Individual',
    /**
     * AL-442 #3: brought in line with the canonical vocabulary.
     *
     * This used substring matching — `r.includes('high')`, `r.includes('med')` —
     * which was more tolerant than every other mapper, so for the same input the
     * blotter could show a rating the database does not hold. It also returned
     * the raw string for anything unrecognised, so a value like 'moderate' was
     * passed straight through to the UI as a rating.
     *
     * Substring matching is the wrong shape regardless of tolerance: 'Non-High
     * Risk' reads as high.
     *
     * The enhancedProfile branch is kept deliberately. It is not a fallback for a
     * bad rating — it is a genuine second source for rows that carry no
     * riskLevel at all, and it is consulted only when riskLevel is absent.
     *
     * Unlike the other read mappers this does not throw, because it runs per row
     * while building the blotter list and one unreadable row must not blank the
     * whole table. It logs and yields undefined, so the row renders without a
     * rating rather than with an invented one.
     */
    riskLevel: (() => {
      const wire = String(raw.riskLevel ?? '').trim();

      if (wire) {
        const parsed = tryParseWireRiskLevelToUpper(wire);
        if (parsed) return parsed.toLowerCase();
        logger.warn('[configService] Blotter row has an unrecognised risk level', {
          id: raw.id ?? raw.caseId ?? '',
          received: wire,
        });
        return undefined;
      }

      // No riskLevel on the row — fall back to enhancedProfile (1=low, 2=medium, 3=high).
      const ep = raw.enhancedProfile ?? raw.enhancedDueDiligence;
      if (ep === 3 || ep === '3' || ep === true) return 'high';
      if (ep === 2 || ep === '2') return 'medium';
      if (ep === 1 || ep === '1') return 'low';
      return undefined;
    })(),
    status: normalizeCDDStatus(
      raw.status,
      raw.reportStatus,
      raw.stage?.status ?? raw.stage,
      raw.currentStage?.status ?? raw.currentStage,
      raw.currentOnboardingStage?.status ?? raw.currentOnboardingStage,
      raw.outcome,
    ),
    createdDate: raw.lastModifiedDate || '',
  };
}

/**
 * AL-511: what the case list can honestly say about its own completeness.
 *
 * The server caps `QueryCases` at 500 and now reports `totalAvailable` and
 * `truncated` alongside the page. Before that it returned the page alone, capped
 * at 100, and the portal presented it as the whole set — a C1 with 107 cases saw
 * 100 and was told nothing.
 *
 * ⚠️ BACKWARD COMPATIBILITY IS THE POINT OF THIS FUNCTION, not a side concern.
 * Live has neither the delta nor the code, so an older server answers with no
 * `totalAvailable` at all. The server also withholds it deliberately in two
 * cases: when a status filter is supplied (the filter runs in Java AFTER the SQL
 * LIMIT, so a pre-limit count would over-report) and when the count query fails.
 *
 * So absent means UNKNOWN and must stay unknown. It must not become `0`, and it
 * must not become "nothing was hidden" — on a compliance surface a false
 * all-clear is worse than no claim, which is the same rule AL-455 landed on for
 * the outstanding-people count.
 *
 * Three states, not two:
 *
 *   known + truncated   -> say so, with both numbers
 *   known + complete    -> say nothing; the list speaks for itself
 *   unknown             -> say nothing; we cannot support a claim either way
 */
export interface CaseListCompleteness {
  /** Rows actually received. Always known. */
  shown: number;
  /** Rows the tenant has, when the server told us. Undefined means unknown. */
  totalAvailable?: number;
  /** True only when the server positively reported truncation. */
  truncated: boolean;
  /** False when the server said nothing, so the UI can stay silent. */
  isKnown: boolean;
}

export function deriveCaseListCompleteness(
  payload: any,
  shown: number,
): CaseListCompleteness {
  const total = payload?.totalAvailable;

  // Strict number check. A string "107" or a null both mean we cannot do
  // arithmetic on it, and coercing would invent a comparison.
  if (typeof total !== 'number' || !Number.isFinite(total)) {
    return { shown, truncated: false, isKnown: false };
  }

  /**
   * `truncated` is read from the server when present, because the server knows
   * whether its own LIMIT bit. It is only derived from the numbers as a fallback
   * for a server that sends the total but not the flag.
   *
   * Not `total > shown` unconditionally: that would also fire if the two
   * disagreed for some other reason, turning an inconsistency into a confident
   * claim about truncation.
   */
  const truncated =
    typeof payload?.truncated === 'boolean' ? payload.truncated : total > shown;

  return { shown, totalAvailable: total, truncated, isKnown: true };
}

/**
 * Fetch CDD records via WebSocket.
 *
 * No `limit` is sent, deliberately. `WebSocketLambda.handleQueryCases` passes 500
 * as a literal and the request schema has no limit field, so a limit here would
 * be silently ignored while reading as though the portal controlled the page
 * size. Pagination is still outstanding on AL-511.
 */
export async function fetchCDDRecords(): Promise<{
  data: any[];
  source: 'real';
  completeness: CaseListCompleteness;
}> {
  ensureWebSocketConnected();
  const response = await eventCentral.sendRequest(socketService, 'QueryCasesRequest', {}, API_CONFIG.REQUEST_TIMEOUT);
  const payload = response.QueryCasesResponse;
  const rawRecords = Array.isArray(payload?.cases) ? payload.cases : [];
  return {
    data: rawRecords.map(mapRawToBlotterRecord),
    source: 'real',
    completeness: deriveCaseListCompleteness(payload, rawRecords.length),
  };
}

/**
 * Fetch a single C2 profile (full detail) via WebSocket
 */
export async function fetchC2Profile(prospectIdentifier: string): Promise<any> {
  ensureWebSocketConnected();
  const response = await eventCentral.sendRequest(
    socketService,
    'CaseReportRequest',
    { caseIdentifier: prospectIdentifier },
    API_CONFIG.REQUEST_TIMEOUT,
  );
  return response.CaseReportResponse ?? null;
}

/**
 * AL-417: fetch the per-person check-account status for a case — who is holding it up.
 *
 * Must be read live rather than cached. The whole value is telling a C1 who to chase, and a stale
 * answer tells them to chase someone who finished last night.
 *
 * Pass the PARENT case identifier. A child case id is refused by the server rather than answered with
 * an empty list, so an empty result here always means "nobody on this case", never "wrong id".
 */
export async function fetchCheckAccounts(prospectIdentifier: string): Promise<any> {
  ensureWebSocketConnected();
  const response = await eventCentral.sendRequest(
    socketService,
    'QueryCheckAccounts',
    { caseIdentifier: prospectIdentifier },
    API_CONFIG.REQUEST_TIMEOUT,
  );
  return response.QueryCheckAccountsResponse ?? null;
}

/**
 * Fetch C2 Invitations via wsApi
 */
export async function fetchInvitations(): Promise<{ data: any[]; source: 'real' }> {
  try {
    const invitations = await wsApi.invitationGetAll();
    return { data: invitations, source: 'real' };
  } catch (error) {
    throw error;
  }
}

/**
 * Fetch My Account data via WebSocket
 */
export async function fetchMyAccount(): Promise<{ data: any; source: 'real' }> {
  ensureWebSocketConnected();
  const response = await eventCentral.sendRequest(socketService, 'QueryMyAccount', {}, API_CONFIG.REQUEST_TIMEOUT);
  return { data: response.QueryMyAccountResponse ?? response.data, source: 'real' };
}

/** Map the latest dev2 QueryInvoices payload into the portal invoice model. */
export function mapBackendInvoice(raw: any): any {
  const backendStatus = String(raw?.status || '').toLowerCase();
  const status = backendStatus === 'paid'
    ? 'paid'
    : backendStatus === 'void' || backendStatus === 'uncollectible' || backendStatus === 'failed'
      ? 'failed'
      : 'pending';

  // Latest backend values are already display-ready (`YYYY-MM-DD` and
  // `$1,234.56 AUD`). Keep compatibility with the older Stripe-shaped payload
  // without treating its `number` field as a backend tax-invoice number.
  const date = raw?.date ?? (
    raw?.created == null
      ? ''
      : new Date(typeof raw.created === 'number' ? raw.created * 1000 : raw.created)
        .toLocaleDateString('en-AU')
  );
  const amount = typeof raw?.amount === 'string'
    ? raw.amount
    : new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: String(raw?.currency || 'AUD').toUpperCase(),
    }).format(Number(raw?.amount ?? 0) / 100);
  // AL-326: keep whatever number the backend issued, whatever its shape. This used to require
  // /^\d{9,10}$/ and discard anything else, which silently dropped manually assigned references such
  // as ARC-2026-0003 — so the row displayed an internal payment id under a header reading "Invoice ID"
  // and offered no download. Validating the format is the database's job, not the mapper's.
  const invoiceNumber = typeof raw?.invoiceNumber === 'string' && raw.invoiceNumber.trim()
    ? raw.invoiceNumber.trim()
    : undefined;

  // Stripe browser receipt URL. Used as a fallback "View Invoice" link when a payment cannot be
  // invoiced through GET_INVOICE at all.
  const pdfUrl = typeof raw?.pdfUrl === 'string' && raw.pdfUrl.trim() ? raw.pdfUrl : undefined;

  return {
    id: String(raw?.id || invoiceNumber || ''),
    invoiceNumber,
    taxInvoiceAvailable: raw?.taxInvoiceAvailable === true,
    pdfUrl,
    date,
    amount,
    status,
  };
}

/**
 * Fetch invoices via WebSocket using the dev2 QueryInvoices contract.
 */
export async function fetchInvoices(): Promise<any[]> {
  try {
    ensureWebSocketConnected();
    const response = await eventCentral.sendRequest(socketService, 'QueryInvoices', {}, API_CONFIG.REQUEST_TIMEOUT);
    const payload = response.QueryInvoicesResponse ?? response.data;
    const invoices = payload?.invoices ?? payload ?? [];
    return Array.isArray(invoices) ? invoices.map(mapBackendInvoice) : [];
  } catch (error) {
    return [];
  }
}

/**
 * Fetch credit usage ledger via WebSocket
 */
export async function fetchUsageLedger(): Promise<{ transactions: any[]; credits: any }> {
  try {
    ensureWebSocketConnected();
    const response = await eventCentral.sendRequest(socketService, 'QueryUsageLedger', {}, API_CONFIG.REQUEST_TIMEOUT);
    const payload = response.QueryUsageLedgerResponse ?? response.data ?? {};
    return { transactions: payload.transactions ?? [], credits: payload.credits ?? null };
  } catch (error) {
    return { transactions: [], credits: null };
  }
}
